import { notify, Table, TableCard, type TableColumn,TableOutput } from "@general-backoffice/core";
import type { ReferralCode } from "@investhinks/api-client";
import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card } from "reactstrap";

import QRCodeModal from "../../qrCode/QRCodeModal";
import AddButtonChildren from "../../shared/AddButtonChildren";

interface ReferralCodesTableCardProps {
  referralCodes: ReferralCode[];
  setReferralCodeToCreate: (code: ReferralCode | null) => void;
  isLoading: boolean;
}

const ReferralCodesTableCard = ({ referralCodes, setReferralCodeToCreate, isLoading }: ReferralCodesTableCardProps) => {
  const [QRToShow, setQRToShow] = React.useState<{ link: string; filename: string } | null>(null);
  const { t } = useTranslation("referrer", { keyPrefix: "partner.referralCodesTableCard" });

  const columns: TableColumn[] = [
    {
      name: "",
      property: "name",
      isTh: true,
    },
    {
      name: t("code"),
      property: "code",
      isTh: true,
      formattedContent: ({ code }) => code?.toUpperCase(),
    },
    {
      name: t("referrals"),
      formattedContent: ({ referralsCount }) => (
        <div style={{ fontSize: "1rem" }}>
          <Badge>
            <span className="mr-2">{referralsCount || "0"}</span>
            <i className="fas fa-user text-default" />
          </Badge>
        </div>
      ),
    },
    {
      name: "",
      formattedContent: (refCode) => {
        const link = `https://investhinks.com?code=${refCode?.code}`;
        return (
          <TableOutput.CustomActions
            actions={[
              {
                icon: <i className="fas fa-qrcode text-default p-1 mr-2" />,
                onClick: () => setQRToShow({ link, filename: `${refCode?.code}_qr_code` }),
                description: t("qrCodeDescription"),
              },
              {
                icon: <i className="fas fa-copy text-default p-1" />,
                onClick: () => {
                  navigator.clipboard.writeText(link);
                  notify.info(t("linkCopied"));
                },
                description: link,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      <QRCodeModal value={QRToShow?.link ?? ""} filename={QRToShow?.filename ?? ""} close={() => setQRToShow(null)} />

      <Card className="overflow-hidden">
        <TableCard.Header
          title={t("title")}
          buttons={[
            {
              children: <AddButtonChildren children={t("createButton")} />,
              onClick: () => setReferralCodeToCreate({ id: 0, code: "" }),
              color: "default",
              size: "md",
            },
          ]}
          isLoading={isLoading}
        />

        <Table
          columns={columns}
          rows={referralCodes}
          isLoading={isLoading}
          noContentMessage={t("emptyCodes")}
          selectable={false}
          sortable={false}
        />
      </Card>
    </>
  );
};

export default ReferralCodesTableCard;
